import React, { useRef, useState } from 'react';
import queryString, { ParsedQuery } from 'query-string';
import { History, Location } from 'history';
import { paths } from 'client/shared/Paths';
import { Footer } from 'client/components/Footer';
import { useBrowserSize } from 'client/contexts/BrowserSize';
import { useCurrentUserCtx } from 'client/contexts/CurrentUser';
import { useGeoRegion } from 'client/hooks/useGeoRegion';
import { useOptimizelyFeature } from 'client/hooks/useOptimizelyFeature';
import {
  RIVERSIDE_ENTRY_POINT_ALLOWED_COUNTRIES,
  VALID_VIDEO_PARTNERS,
} from 'client/screens/IntegrationRedirectScreen/constants';
import { useInView } from 'react-intersection-observer';
import { INTEGRATION_CONNECTION_MENU_MODE } from './constants';
import {
  FAQ,
  Hero,
  LoginSignUp,
  MeetAnchor,
  Process,
  Video,
} from './components';
import {
  trackRiversideLandingPageView,
  trackRiversideScrollLandingPage,
} from './events';

const setReturnToRedirectPath = (data: ParsedQuery<string>) => {
  let partnerIntegrationParams = '';
  if (typeof data.integrationName === 'string') {
    partnerIntegrationParams = queryString.stringify(
      VALID_VIDEO_PARTNERS[data.integrationName].requiredParams.reduce(
        (acc: any, key: string) => {
          return { ...acc, [key]: data[key] };
        },
        {}
      )
    );
  }

  return `videopartner/${data.integrationName}?${encodeURIComponent(
    partnerIntegrationParams
  )}`;
};

export function IntegrationConnectionScreen({
  history,
  location,
}: {
  history: History;
  location: Location;
}) {
  const { width } = useBrowserSize();
  const [isRiversideIntegrationEnabled] = useOptimizelyFeature(
    'riverside_integration',
    '0' // Logged out page userId override
  );
  const { geoRegionData, status: geoRegionStatus } = useGeoRegion();
  const isRiversideIntegrationAllowedCountry = RIVERSIDE_ENTRY_POINT_ALLOWED_COUNTRIES.includes(
    geoRegionData?.geoCountry || ''
  );
  const [videoRef, videoInViewport] = useInView({ triggerOnce: true });

  React.useEffect(() => {
    if (
      geoRegionStatus === 'success' &&
      !isRiversideIntegrationAllowedCountry
    ) {
      history.push(paths.anchor.home);
    }
  }, [isRiversideIntegrationAllowedCountry, geoRegionStatus, history]);

  React.useEffect(() => {
    trackRiversideLandingPageView();
  }, []);

  React.useEffect(() => {
    if (videoInViewport) {
      trackRiversideScrollLandingPage();
    }
  }, [videoInViewport]);

  const integrationData = queryString.parse(
    decodeURIComponent(location.search)
  );
  const partnerName =
    (integrationData.integrationName as string) ||
    location.pathname.replace('/videopartner/', '');

  const [
    currentAuthFormView,
    setCurrentAuthFormView,
  ] = useState<INTEGRATION_CONNECTION_MENU_MODE>('create');
  const {
    state: { status },
    resetCurrentUser,
  } = useCurrentUserCtx();
  const returnToRedirectPath = setReturnToRedirectPath(integrationData);
  const showHeroLoginSignUpForm = width >= 992;

  // If the user currently exists, we need to reset the user and have them
  // login or sign up.
  if (status === 'success') {
    resetCurrentUser();
  }

  const loginSignUpRef = useRef<HTMLDivElement>(null);
  const scrollToLoginSignUp = (ref: any) =>
    ref.current.scrollIntoView({ behavior: 'smooth' });

  return isRiversideIntegrationEnabled &&
    geoRegionStatus === 'success' &&
    isRiversideIntegrationAllowedCountry ? (
    <>
      <Hero
        partnerName={partnerName}
        currentAuthFormView={currentAuthFormView}
        loginSignUpRef={loginSignUpRef}
        setCurrentAuthFormView={setCurrentAuthFormView}
        returnToRedirectPath={returnToRedirectPath}
        showHeroLoginSignUpForm={showHeroLoginSignUpForm}
      />
      {!showHeroLoginSignUpForm && (
        <LoginSignUp
          partnerName={partnerName}
          reference={loginSignUpRef}
          currentAuthFormView={currentAuthFormView}
          setCurrentAuthFormView={setCurrentAuthFormView}
          returnToRedirectPath={returnToRedirectPath}
          showHeroLoginSignUpForm={showHeroLoginSignUpForm}
        />
      )}
      <div style={{ backgroundColor: 'white' }}>
        <Process />
        <Video
          ref={videoRef}
          onClick={() => scrollToLoginSignUp(loginSignUpRef)}
        />
        <MeetAnchor />
        <FAQ />
      </div>
      <Footer />
    </>
  ) : null;
}
